import type { ComponentType, ErrorInfo, FC } from "react";
import { Suspense } from "react";
import { RemoteErrorBoundary } from "@/app/lib/RemoteErrorBoundary";
import type { ComponentProps } from "@/app/module-federation/lib/types";
import { FormSkeleton } from "@/shared/components/FormSkeleton";

interface EntryProps {
    Component: ComponentType<ComponentProps>;
    componentProps: ComponentProps;
    onError?: (error: unknown, errorInfo?: ErrorInfo) => void;
}

/**
 * Entry for hydrating SSR markup on the client.
 *
 * No Suspense wrapper: During SSR, if a component throws an error, React will find the closest
 * <Suspense> above it and include its fallback in the server HTML. By omitting top-level Suspense,
 * the Component itself becomes the shell, enabling proper error handling during SSR.
 *
 * Note: RemoteErrorBoundary only catches errors during client-side rendering (after hydration).
 * Error boundaries do not catch rendering errors during SSR.
 */
export const HydrationEntry: FC<EntryProps> = ({ Component, componentProps, onError }) => (
    <RemoteErrorBoundary onError={onError} formMetadataParams={componentProps.formMetadataParams}>
        <Component {...componentProps} />
    </RemoteErrorBoundary>
);

/**
 * Entry for client-side rendering (CSR) and updates after hydration.
 *
 * Includes Suspense: Used for pure CSR or post-hydration updates where there's no SSR shell.
 * The Suspense boundary allows async components to suspend and show a fallback during loading.
 * RemoteErrorBoundary catches client-side rendering errors after the component mounts.
 */
export const RenderEntry: FC<EntryProps> = ({ Component, componentProps, onError }) => (
    <RemoteErrorBoundary onError={onError} formMetadataParams={componentProps.formMetadataParams}>
        <Suspense fallback={<FormSkeleton />}>
            <Component {...componentProps} />
        </Suspense>
    </RemoteErrorBoundary>
);
